// Typo-tolerant, synonym-aware local search used to power the homepage
// "What's on your mind?" box. Pure client-side string matching — no LLM,
// no network call, so it costs nothing and works offline.

// Common everyday phrasing mapped to the terms actually used in tool
// names/descriptions, so "shrink pdf" or "pic to text" still finds the
// right tool.
export const SYNONYM_MAP: Record<string, string[]> = {
  shrink: ["compress", "reduce", "smaller"],
  compress: ["shrink", "reduce", "smaller"],
  pic: ["image", "photo", "picture"],
  picture: ["image", "photo", "pic"],
  photo: ["image", "picture", "pic"],
  vid: ["video"],
  doc: ["document", "word"],
  pw: ["password"],
  pwd: ["password"],
  qr: ["qr code", "barcode"],
  combine: ["merge", "join"],
  join: ["merge", "combine"],
  cut: ["trim", "crop"],
  crop: ["cut", "trim", "resize"],
  convert: ["converter", "change", "transform"],
  change: ["convert", "converter"],
  calc: ["calculator"],
  gen: ["generator", "generate"],
  generate: ["generator", "gen"],
  make: ["generator", "create", "build"],
  check: ["checker", "validate", "test"],
  test: ["checker", "validate"],
  encode: ["encoder"],
  decode: ["decoder"],
  find: ["search"],
  fix: ["repair", "correct"],
  words: ["word", "text"],
  size: ["dimensions", "resize"],
  bg: ["background"],
  background: ["bg"],
  // Additions covering real gaps across the tool catalog:
  unzip: ["zip", "extract", "archive", "unarchive"],
  zip: ["unzip", "compress", "archive"],
  extract: ["unzip", "pull", "get"],
  rotate: ["turn", "flip"],
  flip: ["rotate", "mirror"],
  resume: ["cv"],
  cv: ["resume"],
  invoice: ["bill"],
  bill: ["invoice"],
  favicon: ["icon"],
  icon: ["favicon"],
  hashtag: ["tag", "tags"],
  case: ["uppercase", "lowercase", "capitalize"],
  lorem: ["placeholder", "dummy", "filler"],
  regex: ["pattern", "expression"],
  hash: ["checksum", "md5", "sha"],
  uuid: ["guid", "id"],
  currency: ["money", "forex", "exchange"],
  unit: ["measurement", "measure"],
  loan: ["mortgage", "emi"],
  tip: ["gratuity", "tipping"],
  grammar: ["spelling", "spellcheck", "proofread"],
  paraphrase: ["reword", "rewrite"],
  svg: ["vector"],
  thumbnail: ["preview", "thumb"],
  split: ["separate", "divide"],
  audio: ["sound", "mp3"],
  ocr: ["scan", "text recognition"],
  youtube: ["yt"],
  yt: ["youtube"],
  excel: ["spreadsheet", "xls", "xlsx"],
  url: ["link"],
  link: ["url"],
  watermark: ["stamp"],
  meme: ["funny image"],
  gif: ["animation", "animated"],
  // Domain-specific additions covering newer/less obvious categories
  taxes: ["tax"],
  budget: ["spending", "expenses", "finances"],
  save: ["savings", "saving"],
  retire: ["retirement", "pension"],
  wedding: ["marriage"],
  vacation: ["trip", "travel", "holiday"],
  packing: ["pack", "suitcase", "luggage"],
  moving: ["relocation", "relocate"],
  grocery: ["groceries", "shopping list"],
  invitation: ["invite"],
  menu: ["restaurant menu"],
  brochure: ["flyer", "pamphlet"],
  banner: ["sign", "poster"],
  sell: ["selling", "seller", "ecommerce"],
  margin: ["profit", "markup"],
  shipping: ["postage", "delivery cost"],
  screenshot: ["screen capture", "capture screen"],
  recording: ["record", "screen record"],
  avatar: ["profile picture", "character"],
  emoji: ["emoticon"],
  sprite: ["game asset"],
  cite: ["citation", "reference", "bibliography"],
  plagiarism: ["similarity", "copied text"],
  patent: ["invention", "disclosure"],
  manual: ["documentation", "user guide", "instructions"],
  mesh: ["3d model", "stl", "obj"],
  contract: ["agreement", "legal document"],
  nda: ["non disclosure", "confidentiality"],
  eviction: ["tenant notice"],
  offer: ["job offer", "employment letter"],
  blocked: ["restricted", "banned", "firewall"],
};

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array(b.length + 1).fill(0).map((_, j) => (i === 0 ? j : 0))
  );
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[a.length][b.length];
}

function expandTerms(word: string): string[] {
  const base = word.toLowerCase();
  return [base, ...(SYNONYM_MAP[base] ?? [])];
}

function fuzzyIncludes(haystack: string, needle: string): boolean {
  if (haystack.includes(needle)) return true;
  if (needle.length < 4) return false;
  // Allow small typos on longer words only (avoids false positives on short words).
  const maxDistance = needle.length <= 6 ? 1 : 2;
  const words = haystack.split(/\s+/);
  return words.some(w => levenshtein(w, needle) <= maxDistance);
}

export interface Searchable {
  name: string;
  description: string;
  category?: string;
}

/** Scores and ranks `items` against `query`. Higher score = better match. */
export function smartSearch<T extends Searchable>(query: string, items: T[]): T[] {
  return smartSearchWithReasons(query, items).map((r) => r.item);
}

export interface SearchResult<T> {
  item: T;
  score: number;
  /** The specific term that produced the strongest match, for showing
   * "matched: compress" style context in richer result UIs. Null if the
   * match came from a whole-query substring match rather than a
   * synonym-expanded term. */
  matchedTerm: string | null;
}

/** Same matching logic as smartSearch, but also reports which term
 * actually caused each match -- used by the richer search UI to show
 * "why" a result matched, and by the "did you mean" fallback. */
export function smartSearchWithReasons<T extends Searchable>(
  query: string,
  items: T[]
): SearchResult<T>[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];

  const queryWords = q.split(/\s+/).filter(Boolean);
  const expandedWords = queryWords.flatMap(expandTerms);

  const scored = items.map((item) => {
    const name = item.name.toLowerCase();
    const description = item.description.toLowerCase();
    let score = 0;
    let bestTerm: string | null = null;
    let bestTermScore = 0;

    if (name === q) {
      score += 100;
      bestTerm = query;
      bestTermScore = 100;
    } else if (name.includes(q)) {
      score += 50;
      bestTerm = query;
      bestTermScore = 50;
    }

    for (const term of expandedWords) {
      if (fuzzyIncludes(name, term)) {
        score += 10;
        if (10 > bestTermScore) {
          bestTerm = term;
          bestTermScore = 10;
        }
      }
      if (fuzzyIncludes(description, term)) {
        score += 3;
        if (3 > bestTermScore && !bestTerm) {
          bestTerm = term;
          bestTermScore = 3;
        }
      }
    }

    return { item, score, matchedTerm: bestTerm };
  });

  return scored.filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
}

/** Closest near-misses for a query that returned few or no results --
 * powers the "did you mean" fallback. Loosens the fuzzy-match distance
 * rather than requiring an exact substring. */
export function suggestCloseMatches<T extends Searchable>(
  query: string,
  items: T[],
  limit = 3
): T[] {
  const q = query.toLowerCase().trim();
  if (q.length < 3) return [];

  const scored = items.map((item) => {
    const name = item.name.toLowerCase();
    const words = name.split(/\s+/);
    const minDist = Math.min(...words.map((w) => levenshtein(w, q)), levenshtein(name, q));
    return { item, minDist };
  });

  return scored
    .filter((s) => s.minDist > 0 && s.minDist <= 3)
    .sort((a, b) => a.minDist - b.minDist)
    .slice(0, limit)
    .map((s) => s.item);
}
